import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, RefreshCw, Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { getH1 } from '../seo/seoRoutes.mjs';

const LoveSpreadPage = () => {
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();
  const [selectedCardCount, setSelectedCardCount] = useState(null);
  const [selectedRelationshipType, setSelectedRelationshipType] = useState(null);
  const [question, setQuestion] = useState('');
  const [currentQuote, setCurrentQuote] = useState('');

  // Card count options with their meanings
  const cardOptions = [
    { count: 1, name: t('cards.singleCard'), description: t('cards.singleCardDesc') },
    { count: 3, name: t('cards.pastPresentFuture'), description: t('cards.pastPresentFutureDesc') },
    { count: 5, name: t('cards.loveCross'), description: t('cards.loveCrossDesc') },
    { count: 6, name: t('cards.relationshipSpread'), description: t('cards.relationshipSpreadDesc') },
    { count: 7, name: t('cards.chakraLove'), description: t('cards.chakraLoveDesc') },
    { count: 10, name: t('cards.celticCross'), description: t('cards.celticCrossDesc') },
    { count: 12, name: t('cards.zodiacLove'), description: t('cards.zodiacLoveDesc') }
  ];

  // Relationship type options
  const relationshipTypes = [
    {
      type: "singles",
      name: t('love.singles'),
      icon: "icon_01_find_v2.png",
      color: "#FFFDDE",
      description: t('love.singlesDesc'),
      focusAreas: [t('love.selfLovePrep'), t('love.attractPartner'), t('love.removeLoveBlocks')]
    },
    {
      type: "couple",
      name: t('love.couple'),
      icon: "icon_02_love_v2.png",
      color: "#FFCBCB",
      description: t('love.coupleDesc'),
      focusAreas: [t('love.communication'), t('love.intimacy'), t('love.relationshipGrowth')]
    },
    {
      type: "family",
      name: t('love.family'),
      icon: "icon_03_family_v2.png",
      color: "#E7FBBE",
      description: t('love.familyDesc'),
      focusAreas: [t('love.familyDynamics'), t('love.healingOldWounds'), t('love.creatingHarmony')]
    },
    {
      type: "business",
      name: t('love.business'),
      icon: "icon_04_biz_v2.png",
      color: "#7BD3EA",
      description: t('love.businessDesc'),
      focusAreas: [t('love.networking'), t('love.partnerships'), t('love.professionalBounds')]
    }
  ];

  // Spread interpretations based on relationship type and card count
  const getSpreadInterpretations = (type, count) => {
    const interpretations = {
      singles: {
        1: [t('loveSpread.singles.1.loveReadiness')],
        3: [t('loveSpread.singles.3.pastLessons'), t('loveSpread.singles.3.currentEnergy'), t('loveSpread.singles.3.futureLove')],
        5: [t('loveSpread.singles.5.yourDesire'), t('loveSpread.singles.5.whatAttractsLove'), t('loveSpread.singles.5.loveBlocks'), t('loveSpread.singles.5.actionToTake'), t('loveSpread.singles.5.loveManifestation')],
        6: [t('loveSpread.singles.6.selfLoveStatus'), t('loveSpread.singles.6.whatYouSeek'), t('loveSpread.singles.6.whatYouOffer'), t('loveSpread.singles.6.currentObstacles'), t('loveSpread.singles.6.divineTiming'), t('loveSpread.singles.6.loveOutcome')],
        7: [t('loveSpread.singles.7.rootSecurity'), t('loveSpread.singles.7.sacralDesires'), t('loveSpread.singles.7.solarConfidence'), t('loveSpread.singles.7.heartCapacity'), t('loveSpread.singles.7.throatExpression'), t('loveSpread.singles.7.thirdEyeIntuition'), t('loveSpread.singles.7.crownConnection')],
        10: [t('loveSpread.singles.10.loveSituation'), t('loveSpread.singles.10.challengeCross'), t('loveSpread.singles.10.distantPast'), t('loveSpread.singles.10.recentPast'), t('loveSpread.singles.10.possibleOutcome'), t('loveSpread.singles.10.nearFuture'), t('loveSpread.singles.10.yourApproach'), t('loveSpread.singles.10.externalInfluences'), t('loveSpread.singles.10.hopesFears'), t('loveSpread.singles.10.finalOutcome')],
        12: [t('loveSpread.singles.12.janNewLoveEnergy'), t('loveSpread.singles.12.febSelfLove'), t('loveSpread.singles.12.marTakingAction'), t('loveSpread.singles.12.aprBuildingFoundation'), t('loveSpread.singles.12.mayAdventure'), t('loveSpread.singles.12.junHarmony'), t('loveSpread.singles.12.julReflection'), t('loveSpread.singles.12.augStrength'), t('loveSpread.singles.12.sepCompletion'), t('loveSpread.singles.12.octTransformation'), t('loveSpread.singles.12.novInnerWork'), t('loveSpread.singles.12.decLoveManifestation')]
      },
      couple: {
        1: [t('loveSpread.couple.1.relationshipEnergy')],
        3: [t('loveSpread.couple.3.relationshipPast'), t('loveSpread.couple.3.currentDynamic'), t('loveSpread.couple.3.futureTogether')],
        5: [t('loveSpread.couple.5.youInRelationship'), t('loveSpread.couple.5.yourPartner'), t('loveSpread.couple.5.relationshipBond'), t('loveSpread.couple.5.challengeToAddress'), t('loveSpread.couple.5.relationshipFuture')],
        6: [t('loveSpread.couple.6.yourFeelings'), t('loveSpread.couple.6.partnersFeelings'), t('loveSpread.couple.6.relationshipFoundation'), t('loveSpread.couple.6.currentChallenge'), t('loveSpread.couple.6.whatsNeeded'), t('loveSpread.couple.6.relationshipPath')],
        7: [t('loveSpread.couple.7.rootSecurity'), t('loveSpread.couple.7.sacralIntimacy'), t('loveSpread.couple.7.solarConfidence'), t('loveSpread.couple.7.heartLove'), t('loveSpread.couple.7.throatCommunication'), t('loveSpread.couple.7.thirdEyeVision'), t('loveSpread.couple.7.crownBond')],
        10: [t('loveSpread.couple.10.relationshipStatus'), t('loveSpread.couple.10.challengeCross'), t('loveSpread.couple.10.foundation'), t('loveSpread.couple.10.recentPast'), t('loveSpread.couple.10.possibleOutcome'), t('loveSpread.couple.10.nearFuture'), t('loveSpread.couple.10.yourRole'), t('loveSpread.couple.10.externalInfluences'), t('loveSpread.couple.10.hopesFears'), t('loveSpread.couple.10.relationshipFuture')],
        12: [t('loveSpread.couple.12.janNewBeginnings'), t('loveSpread.couple.12.febGrowthTogether'), t('loveSpread.couple.12.marTakingAction'), t('loveSpread.couple.12.aprStability'), t('loveSpread.couple.12.mayAdventure'), t('loveSpread.couple.12.junHarmony'), t('loveSpread.couple.12.julReflection'), t('loveSpread.couple.12.augStrength'), t('loveSpread.couple.12.sepCompletion'), t('loveSpread.couple.12.octChange'), t('loveSpread.couple.12.novDeepWork'), t('loveSpread.couple.12.decCelebration')]
      },
      family: {
        1: [t('loveSpread.family.1.familyEnergy')],
        3: [t('loveSpread.family.3.familyPast'), t('loveSpread.family.3.currentDynamic'), t('loveSpread.family.3.familyFuture')],
        5: [t('loveSpread.family.5.yourRole'), t('loveSpread.family.5.familyFoundation'), t('loveSpread.family.5.currentChallenge'), t('loveSpread.family.5.healingNeeded'), t('loveSpread.family.5.familyHarmony')],
        6: [t('loveSpread.family.6.yourFeelings'), t('loveSpread.family.6.familysNeeds'), t('loveSpread.family.6.familyStrengths'), t('loveSpread.family.6.currentIssues'), t('loveSpread.family.6.healingPath'), t('loveSpread.family.6.familyFuture')],
        7: [t('loveSpread.family.7.rootSecurity'), t('loveSpread.family.7.sacralCreativity'), t('loveSpread.family.7.solarPower'), t('loveSpread.family.7.heartLove'), t('loveSpread.family.7.throatCommunication'), t('loveSpread.family.7.thirdEyeWisdom'), t('loveSpread.family.7.crownSpirit')],
        10: [t('loveSpread.family.10.familySituation'), t('loveSpread.family.10.challengeCross'), t('loveSpread.family.10.familyFoundation'), t('loveSpread.family.10.recentPast'), t('loveSpread.family.10.possibleOutcome'), t('loveSpread.family.10.nearFuture'), t('loveSpread.family.10.yourRole'), t('loveSpread.family.10.externalInfluences'), t('loveSpread.family.10.familyHopesFears'), t('loveSpread.family.10.familyFuture')],
        12: [t('loveSpread.family.12.janNewFamilyStart'), t('loveSpread.family.12.febFamilyGrowth'), t('loveSpread.family.12.marFamilyAction'), t('loveSpread.family.12.aprFamilyStability'), t('loveSpread.family.12.mayFamilyFun'), t('loveSpread.family.12.junFamilyHarmony'), t('loveSpread.family.12.julFamilyReflection'), t('loveSpread.family.12.augFamilyStrength'), t('loveSpread.family.12.sepFamilyCompletion'), t('loveSpread.family.12.octFamilyChange'), t('loveSpread.family.12.novFamilyHealing'), t('loveSpread.family.12.decFamilyJoy')]
      },
      business: {
        1: [t('loveSpread.business.1.businessRelationship')],
        3: [t('loveSpread.business.3.professionalPast'), t('loveSpread.business.3.currentDynamic'), t('loveSpread.business.3.professionalFuture')],
        5: [t('loveSpread.business.5.yourProfessionalRole'), t('loveSpread.business.5.partnerColleague'), t('loveSpread.business.5.businessFoundation'), t('loveSpread.business.5.challengeToAddress'), t('loveSpread.business.5.professionalOutcome')],
        6: [t('loveSpread.business.6.yourProfessionalFeelings'), t('loveSpread.business.6.theirPerspective'), t('loveSpread.business.6.businessFoundation'), t('loveSpread.business.6.currentChallenge'), t('loveSpread.business.6.strategyNeeded'), t('loveSpread.business.6.businessFuture')],
        7: [t('loveSpread.business.7.rootSecurity'), t('loveSpread.business.7.sacralCollaboration'), t('loveSpread.business.7.solarPower'), t('loveSpread.business.7.heartRespect'), t('loveSpread.business.7.throatCommunication'), t('loveSpread.business.7.thirdEyeVision'), t('loveSpread.business.7.crownPurpose')],
        10: [t('loveSpread.business.10.businessSituation'), t('loveSpread.business.10.challengeCross'), t('loveSpread.business.10.businessFoundation'), t('loveSpread.business.10.recentPast'), t('loveSpread.business.10.possibleOutcome'), t('loveSpread.business.10.nearFuture'), t('loveSpread.business.10.yourApproach'), t('loveSpread.business.10.externalInfluences'), t('loveSpread.business.10.businessHopesFears'), t('loveSpread.business.10.professionalFuture')],
        12: [t('loveSpread.business.12.janNewBusinessEnergy'), t('loveSpread.business.12.febProfessionalGrowth'), t('loveSpread.business.12.marBusinessAction'), t('loveSpread.business.12.aprStableFoundation'), t('loveSpread.business.12.mayBusinessAdventure'), t('loveSpread.business.12.junProfessionalHarmony'), t('loveSpread.business.12.julBusinessReflection'), t('loveSpread.business.12.augBusinessStrength'), t('loveSpread.business.12.sepProjectCompletion'), t('loveSpread.business.12.octBusinessTransformation'), t('loveSpread.business.12.novStrategicPlanning'), t('loveSpread.business.12.decBusinessSuccess')]
      }
    };
    
    return interpretations[type]?.[count] || interpretations.singles[count] || [t('love.yourLoveGuidance')];
  };

  useEffect(() => {
    // Set a random quote when component mounts
    const loveQuotes = t('quotes.love');
    const randomQuote = loveQuotes[Math.floor(Math.random() * loveQuotes.length)];
    setCurrentQuote(randomQuote);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  // Auto-scroll to relationship type section when card count is selected
  useEffect(() => {
    if (selectedCardCount) {
      const relationshipSection = document.querySelector('.relationship-type-section');
      if (relationshipSection) {
        relationshipSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [selectedCardCount]);

  // Auto-scroll to question section when relationship type is selected
  useEffect(() => {
    if (selectedRelationshipType) {
      const questionSection = document.querySelector('.question-section');
      if (questionSection) {
        questionSection.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    }
  }, [selectedRelationshipType]);

  const startLoveReading = () => {
    if (!selectedCardCount || !selectedRelationshipType) return;
    
    // Navigate to SpreadDrawingPage with love spread configuration
    navigate('/spread-drawing', {
      state: {
        spreadName: `${relationshipTypes.find(t => t.type === selectedRelationshipType)?.name} - ${cardOptions.find(o => o.count === selectedCardCount)?.name}`,
        cardCount: selectedCardCount,
        spreadType: "love",
        spreadInterpretations: getSpreadInterpretations(selectedRelationshipType, selectedCardCount),
        question: question,
        tarotCards: [], // Use default cards from SpreadDrawingPage
        returnPath: "/love-spread",
        spreadConfig: {
          relationshipType: selectedRelationshipType,
          isLoveReading: true
        }
      }
    });
  };

  const resetSpread = () => {
    setSelectedCardCount(null);
    setSelectedRelationshipType(null);
    setQuestion('');
    
    // Set new random quote
    const loveQuotes = t('quotes.love');
    const randomQuote = loveQuotes[Math.floor(Math.random() * loveQuotes.length)];
    setCurrentQuote(randomQuote);
  };

  return (
    <div className="love-spread-page">
      {/* Welcome Section */}
      <section className="welcome-section">
        <div className="welcome-content">
          <div className="welcome-text">
            <h1 className="welcome-subtitle">
              {getH1('/love-spread', currentLanguage)}
            </h1>
            <blockquote className="inspiration-question">
              {currentQuote}
            </blockquote>
            <button 
              className="draw-cards-btn"
              onClick={() => {
                navigate('/spread-drawing', { 
                  state: { 
                    spreadName: t('love.loveCardOfDay'),
                    spreadType: 'single',
                    cardCount: 1,
                    spreadInterpretations: [t('dailyCard.lovePosition')],
                    question: currentQuote,
                    returnPath: "/love-spread",
                    spreadConfig: {
                      isSingleCard: true,
                      isDailyCard: true
                    }
                  } 
                })
              }}
            >
              <Sparkles size={20} />
              {t('love.drawLoveCard')}
            </button>
          </div>
          <div className="welcome-decoration"></div>
        </div>
      </section>

      <div className="card-selection-section">
        <h2>1. {t('love.chooseLoveSpread')}</h2>
        <p className="selection-subtitle">{t('love.selectCardCount')}</p>
        
        <div className="card-options-grid">
          {cardOptions.map((option) => (
            <div
              key={option.count}
              className={`card-option ${selectedCardCount === option.count ? 'selected' : ''}`}
              onClick={() => setSelectedCardCount(option.count)}
            >
              <span className="card-count">{option.count}</span>
              <div className="option-content">
                <span className="option-name">{option.name}</span>
                <p className="option-description">{option.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Relationship Type Section */}
        <div className="relationship-type-section">
          <h2>2. {t('love.selectRelationship')}</h2>
          <p className="selection-subtitle">{t('love.selectSituation')}</p>
          
          <div className="relationship-types-grid">
            {relationshipTypes.map((relType) => (
              <div
                key={relType.type}
                className={`relationship-type-card ${selectedRelationshipType === relType.type ? 'selected' : ''}`}
                onClick={() => setSelectedRelationshipType(relType.type)}
                style={{ '--type-color': relType.color }}
              >
                <div className="type-icon">
                  <img 
                    src={`/images/${relType.icon}`} 
                    alt={relType.name}
                    style={{ width: '3rem', height: '3rem' }}
                  />
                </div>
                <h3 className="type-name">{relType.name}</h3>
                <p className="type-description">{relType.description}</p>
                <div className="focus-areas">
                  <small>{t('love.focusAreas')}</small>
                  <ul>
                    {relType.focusAreas.map((area, index) => (
                      <li key={index}>{area}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {selectedCardCount && selectedRelationshipType && (
          <div className="question-section">
            <h2>3. {t('love.askLoveQuestion')}</h2>
            <p className="selection-subtitle">{t('spread.questionPlaceholder')}</p>
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={t('love.questionPlaceholder')}
              className="question-input"
              rows="3"
            />
            
            <button 
              onClick={startLoveReading} 
              className="draw-cards-btn"
            >
              <Heart size={20} />
              {t('love.startLoveReading')}
            </button>

            <button 
              onClick={resetSpread} 
              className="secondary-btn"
              style={{ marginLeft: '1rem' }}
            >
              <RefreshCw size={16} />
              {t('love.reset')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoveSpreadPage;
